/**
 * Description d'une page à partir de son adresse : titre, fil d'Ariane, nœud du catalogue, métier.
 * Les fiches d'objet (/referentiels/tiers/<id>…) sont rattachées au nœud dont la route est le plus long préfixe ;
 * leur titre vient de l'écran (useTitrePage).
 */
import { noeud as trouverNoeud, noeudDeRoute, noeuds, type MetierId, type Noeud } from '@agora/shared';
import type { BreadcrumbProps } from '../../design';
import type { Traduire } from '../i18n';
import { ROUTE_NOTIFICATIONS } from '../notifications';

export interface DescriptionPage {
  titre: string;
  fil: BreadcrumbProps['items'];
  noeud?: Noeud;
  metier?: MetierId;
  /** Libellé du module (pages ouvertes, étiquettes). */
  module?: string;
  /** Fiche d'un objet rattachée au nœud (route plus longue que celle du nœud). */
  fiche?: boolean;
}

function decrireNoeud(n: Noeud, t: Traduire, lib: (id: string) => string): DescriptionPage {
  if (n.type === 'Dimension') {
    return { titre: lib(n.id), noeud: n, module: t('nav.referentiels'), fil: [{ label: t('nav.referentiels'), href: '/referentiels/tiers' }, { label: lib(n.id) }] };
  }
  if (!n.metier) {
    const section = n.section === 'outils' ? t('nav.outils') : t('nav.pilotage');
    return { titre: lib(n.id), noeud: n, module: section, fil: [{ label: section }, { label: lib(n.id) }] };
  }
  const ancetres = [...n.parents].reverse().map((id) => trouverNoeud(id)).filter((x): x is Noeud => !!x);
  const fil: BreadcrumbProps['items'] = ancetres.map((a, i) => ({ label: lib(a.id), href: a.route, metier: i === 0 ? n.metier : undefined }));
  fil.push({ label: lib(n.id), metier: fil.length === 0 ? n.metier : undefined });
  const module = n.type === 'SousModule' ? lib(n.parents[0]) : lib(n.id);
  return { titre: lib(n.id), fil, noeud: n, metier: n.metier, module };
}

export function decrirePage(chemin: string, t: Traduire, lib: (id: string) => string, titrePropre?: string): DescriptionPage {
  const propre = chemin.replace(/\/+$/, '') || '/';
  const avecTitre = (d: DescriptionPage): DescriptionPage => (titrePropre ? { ...d, titre: titrePropre } : d);
  if (propre === '/') return avecTitre({ titre: t('nav.accueil'), fil: [] });
  if (propre === '/reglages') return avecTitre({ titre: t('reglages.titre'), module: t('reglages.titre'), fil: [{ label: t('reglages.titre') }] });
  if (propre === ROUTE_NOTIFICATIONS) {
    return avecTitre({
      titre: t('notifications.titre'), module: t('profil.monProfil'),
      fil: [{ label: t('profil.monProfil'), href: '/reglages#compte' }, { label: t('notifications.titre') }],
    });
  }
  if (propre === '/administration/modules') {
    return avecTitre({ titre: t('admin.modules.titre'), module: t('nav.administration'), fil: [{ label: t('nav.administration') }, { label: t('admin.modules.titre') }] });
  }
  if (propre === '/administration/droits') {
    return avecTitre({ titre: t('admin.droits.titre'), module: t('nav.administration'), fil: [{ label: t('nav.administration') }, { label: t('admin.droits.titre') }] });
  }
  const n = noeudDeRoute(propre);
  if (n) return avecTitre(decrireNoeud(n, t, lib));
  // Fiche d'un objet : nœud dont la route est le plus long préfixe du chemin.
  const parent = noeuds()
    .filter((x) => propre.startsWith(`${x.route}/`))
    .sort((a, b) => b.route.length - a.route.length)[0];
  if (parent) {
    const d = decrireNoeud(parent, t, lib);
    const fil = d.fil.map((it, i) => (i === d.fil.length - 1 ? { ...it, href: parent.route } : it));
    const titre = titrePropre || lib(parent.id);
    return { ...d, titre, fiche: true, fil: [...fil, { label: titre }] };
  }
  return { titre: t('erreurs.pageIntrouvable'), fil: [{ label: t('erreurs.pageIntrouvable') }] };
}
